import { motion } from "framer-motion";
import {
  Phone,
  BarChart3,
  Search,
  FileSearch,
  Landmark,
  FileCheck,
  Settings,
  ArrowRight,
} from "lucide-react";

const steps = [
  {
    icon: Phone,
    title: "Qualification Call",
    duration: "30 min",
    description: "Income profile assessment. Honest fit evaluation. We tell you if this isn't the right time.",
    detail: "This is a mutual assessment — not a sales call. We evaluate your income, tax bracket, and investment readiness. If we don't see a fit, we'll tell you honestly.",
  },
  {
    icon: BarChart3,
    title: "Strategy Session",
    duration: "60 min",
    description: "Real numbers. 2-3 scenarios modelled. Full AfA calculations against your tax bracket.",
    detail: "Using your actual salary and tax data, we model 2-3 complete investment scenarios with depreciation benefits, monthly cash flow, and 10-year equity buildup.",
  },
  {
    icon: Search,
    title: "Property Shortlist",
    duration: "1-2 weeks",
    description: "Off-market only. 1-3 pre-vetted opportunities matching your criteria and goals.",
    detail: "We source from exclusive developer networks — properties not available on public listings. Each vetted against strict criteria: location, quality, rental demand, tax potential.",
  },
  {
    icon: FileSearch,
    title: "Due Diligence",
    duration: "2-3 weeks",
    description: "Location analysis, structural review, rental validation, legal checks. All coordinated.",
    detail: "Comprehensive due diligence: micro-location analysis, building assessment, rental market validation, legal verification, and energy efficiency review.",
  },
  {
    icon: Landmark,
    title: "Financing",
    duration: "2-4 weeks",
    description: "Best terms from 200+ banks. Loan structure aligned to your investment strategy.",
    detail: "We negotiate across 200+ institutions to secure optimal rates and LTV ratios tailored to your strategy.",
  },
  {
    icon: FileCheck,
    title: "Notary & Purchase",
    duration: "4-6 weeks",
    description: "Documents translated, notary managed, land register handled. You sign, we execute.",
    detail: "Contract drafting, notary scheduling, document translation, land register entry, payment processing — we handle everything.",
  },
  {
    icon: Settings,
    title: "Management",
    duration: "Ongoing",
    description: "Tenant sourcing, rent collection, maintenance. Monthly reporting. Full service.",
    detail: "Post-purchase: tenant sourcing, lease agreements, rent collection, maintenance coordination, and monthly financial reporting.",
  },
];

const ProcessPage = () => {
  return (
    <div className="min-h-screen bg-ire-black pt-32 pb-24" data-testid="process-page">
      {/* Hero */}
      <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px w-16 bg-ire-accent" />
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">
              The Process
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extralight text-white leading-[1.1] mb-6">
            From first call
            <br />
            <span className="text-white/40">to keys in hand.</span>
          </h1>
          <p className="font-body text-base lg:text-lg text-white/50 max-w-2xl leading-relaxed">
            Seven steps, each fully managed. The typical timeline from qualification
            to ownership is 10-16 weeks. Here's exactly what happens at each stage.
          </p>
        </motion.div>
      </div>

      {/* Steps — alternating card layout */}
      <div className="max-w-[1100px] mx-auto px-8 md:px-16 lg:px-24">
        {steps.map((step, index) => {
          const Icon = step.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="relative mb-4"
              data-testid={`process-page-step-${index + 1}`}
            >
              <div className={`border border-white/[0.06] p-6 lg:p-8 hover:border-white/[0.1] transition-colors duration-500 ${index % 2 === 0 ? "bg-ire-black" : "bg-ire-surface"}`}>
                <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                  {/* Step indicator */}
                  <div className="flex items-center gap-4 sm:flex-col sm:items-center sm:min-w-[64px]">
                    <div className="w-12 h-12 border border-ire-accent/30 bg-ire-accent/5 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-ire-accent" />
                    </div>
                    <div className="flex items-center gap-2 sm:mt-2">
                      <span className="font-body text-[10px] tracking-[0.15em] text-white/20 uppercase">Step {index + 1}</span>
                      <span className="hidden sm:inline font-body text-[10px] text-ire-accent/60">|</span>
                      <span className="font-body text-[10px] text-ire-accent">{step.duration}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="font-heading text-xl lg:text-2xl font-extralight text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="font-body text-sm text-white/50 leading-relaxed mb-2">
                      {step.description}
                    </p>
                    <p className="font-body text-xs text-white/25 leading-relaxed">
                      {step.detail}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}

        {/* CTA */}
        <motion.a
          href="#consultation"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="group relative flex items-center justify-between mt-8 p-10 border border-ire-accent/20 bg-gradient-to-r from-ire-accent/[0.04] via-transparent to-ire-accent/[0.04] overflow-hidden hover:border-ire-accent/40 transition-all duration-700"
        >
          {/* Glow orbs */}
          <div className="absolute -left-20 -top-20 w-60 h-60 bg-ire-accent/10 rounded-full blur-[80px] group-hover:bg-ire-accent/20 transition-all duration-700" />
          <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-ire-accent/8 rounded-full blur-[60px] group-hover:bg-ire-accent/15 transition-all duration-700" />
          {/* Scan line */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ire-accent/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ire-accent/60 to-transparent" />
          </div>
          <div className="relative z-10">
            <p className="font-body text-[10px] tracking-[0.3em] text-ire-accent/70 uppercase mb-2">Ready to begin?</p>
            <p className="font-heading text-2xl lg:text-3xl text-white group-hover:text-ire-accent transition-colors duration-500">
              Start at Step 1 — Book Your Call
            </p>
          </div>
          <div className="relative z-10 w-14 h-14 border border-ire-accent/30 flex items-center justify-center group-hover:bg-ire-accent group-hover:border-ire-accent transition-all duration-500 shrink-0">
            <ArrowRight className="w-5 h-5 text-ire-accent group-hover:text-black group-hover:translate-x-1 transition-all duration-300" />
          </div>
        </motion.a>
      </div>
    </div>
  );
};

export default ProcessPage;
